/**
 * PlantOverviewStore
 *
 * TODO: When dashboard-metrics API is available, replace generatePlantMetrics()
 * with a call to the dashboard-metrics service for server-side calculated metrics.
 * See: /src/types/api-types/financial/dashboard-metrics/dashboard-metrics.types.ts
 *
 * The dashboardMetrics type provides pre-calculated per-plant metrics:
 * - revenue.mtd, revenue.ytd
 * - ar.outstanding, ar.overdue
 * - ap.outstanding, ap.overdue
 * - openPOs, pendingInvoices, cashPosition
 */
import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";

// Base Plant interface for local use
// Note: Real API type is at types/api-types/organization-data/plants/plants.types.ts
interface Plant {
  id: string;
  name: string;
  location?: string;
  manager?: string;
  status?: "active" | "inactive" | "maintenance";
  createdAt: string;
  updatedAt: string;
}

// Extended Plant type with financial metrics per spec 01-multi-plant-dashboard.md
export interface PlantWithMetrics extends Plant {
  code?: string; // Plant code (e.g., "100", "200", "300")
  metrics?: PlantMetrics;
}

export interface PlantMetrics {
  // Revenue metrics
  revenueMTD: number;
  revenueYTD: number;
  // Accounts Receivable
  arOutstanding: number;
  arOverdue: number;
  // Accounts Payable
  apOutstanding: number;
  apOverdue: number;
  // Operations
  openPOs: number;
  pendingInvoices: number;
  cashPosition: number;
  // Timestamps
  lastUpdated: Date;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export default class PlantOverviewStore {
  // Observables
  plants: PlantWithMetrics[] = [];
  selectedPlantId: string | null = null;
  isLoading = false;
  error: string | null = null;
  filterStatus: "all" | "active" | "inactive" | "maintenance" = "all";
  searchQuery = "";
  timeRange: "month" | "quarter" | "year" = "year";

  // Date range for filtering (per spec D-06)
  dateRange: DateRange = {
    start: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    end: new Date(),
  };

  // CFO-relevant metrics from various services (aggregated across all plants)
  outstandingInvoices = 0;
  overduePayments = 0;
  inventoryTurnoverRate = 0;
  pendingPurchaseOrders = 0;
  qualityDefectRate = 0;
  onTimeDeliveryRate = 0;
  cashFlowPosition = 0;
  operatingMargin = 0;
  workforceEfficiency = 0;
  maintenanceCosts = 0;
  energyCostPerUnit = 0;
  customerRetentionRate = 0;

  // Aggregated AR/AP metrics (from all plants)
  totalAROutstanding = 0;
  totalAROverdue = 0;
  totalAPOutstanding = 0;
  totalAPOverdue = 0;
  totalOpenPOs = 0;
  totalPendingInvoices = 0;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as any);
    // Don't auto-initialize - let the route handle it
  }

  // Actions
  setSelectedPlant(plantId: string | null) {
    this.selectedPlantId = plantId;
  }

  setFilterStatus(status: "all" | "active" | "inactive" | "maintenance") {
    this.filterStatus = status;
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  setTimeRange(range: "month" | "quarter" | "year") {
    this.timeRange = range;
  }

  setDateRange(start: Date, end: Date) {
    this.dateRange = { start, end };
    // Refresh metrics when date range changes
    this.refreshPlantMetrics();
  }

  clearError() {
    this.error = null;
  }

  /**
   * Calculate real plant metrics from invoices and bills filtered by plantId
   * TODO: Replace with dashboard-metrics API call when available
   */
  private calculatePlantMetricsFromFinancialData(plantId: string): PlantMetrics | null {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const invoicesStore = this.rootStore.invoicesStore as any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const billsStore = this.rootStore.billsStore as any;

      if (!invoicesStore?.entities?.length && !billsStore?.entities?.length) {
        return null; // No financial data available
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const invoices = (invoicesStore?.entities || []) as any[];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bills = (billsStore?.entities || []) as any[];

      // Filter by plantId
      const plantInvoices = invoices.filter((inv) => inv.plantId === plantId);
      const plantBills = bills.filter((bill) => bill.plantId === plantId);

      // No plant-specific data available
      if (plantInvoices.length === 0 && plantBills.length === 0) {
        return null;
      }

      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const todayStr = today.toISOString().split("T")[0];

      // Calculate revenue MTD (paid invoices this month)
      const revenueMTD = plantInvoices
        .filter((inv) => {
          const paidDate = inv.paidDate || inv.updatedAt;
          return inv.status === "paid" && paidDate && new Date(paidDate) >= startOfMonth;
        })
        .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

      // Calculate revenue YTD (paid invoices this year)
      const revenueYTD = plantInvoices
        .filter((inv) => {
          const paidDate = inv.paidDate || inv.updatedAt;
          return inv.status === "paid" && paidDate && new Date(paidDate) >= startOfYear;
        })
        .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

      // Calculate AR outstanding (unpaid invoices)
      const arOutstanding = plantInvoices
        .filter((inv) => inv.status !== "paid" && inv.status !== "cancelled")
        .reduce((sum, inv) => sum + (inv.amountDue || inv.totalAmount || 0), 0);

      // Calculate AR overdue
      const arOverdue = plantInvoices
        .filter((inv) => {
          const dueDate = inv.dueDate;
          return inv.status !== "paid" && dueDate && dueDate < todayStr;
        })
        .reduce((sum, inv) => sum + (inv.amountDue || inv.totalAmount || 0), 0);

      // Calculate AP outstanding (unpaid bills)
      const apOutstanding = plantBills
        .filter((bill) => bill.status !== "paid" && bill.status !== "cancelled")
        .reduce((sum, bill) => sum + (bill.amountDue || bill.totalAmount || 0), 0);

      // Calculate AP overdue
      const apOverdue = plantBills
        .filter((bill) => {
          const dueDate = bill.dueDateTime?.split("T")[0] || bill.dueDate;
          return bill.status !== "paid" && dueDate && dueDate < todayStr;
        })
        .reduce((sum, bill) => sum + (bill.amountDue || bill.totalAmount || 0), 0);

      // Count pending invoices
      const pendingInvoices = plantInvoices.filter(
        (inv) => inv.status === "draft" || inv.status === "sent"
      ).length;

      return {
        revenueMTD,
        revenueYTD,
        arOutstanding,
        arOverdue,
        apOutstanding,
        apOverdue,
        openPOs: 0, // TODO: integrate with purchase-orders store when available
        pendingInvoices,
        cashPosition: revenueMTD - apOutstanding, // Simple calculation
        lastUpdated: new Date(),
      };
    } catch (error) {
      return null;
    }
  }

  // Return empty metrics when no financial data is available
  private getEmptyMetrics(): PlantMetrics {
    return {
      revenueMTD: 0,
      revenueYTD: 0,
      arOutstanding: 0,
      arOverdue: 0,
      apOutstanding: 0,
      apOverdue: 0,
      openPOs: 0,
      pendingInvoices: 0,
      cashPosition: 0,
      lastUpdated: new Date(),
    };
  }

  // Aggregate metrics from all plants
  private aggregatePlantMetrics() {
    let totalAR = 0;
    let totalAROverdue = 0;
    let totalAP = 0;
    let totalAPOverdue = 0;
    let totalPOs = 0;
    let totalInvoices = 0;
    let totalCash = 0;

    this.plants.forEach((plant) => {
      if (plant.metrics) {
        totalAR += plant.metrics.arOutstanding;
        totalAROverdue += plant.metrics.arOverdue;
        totalAP += plant.metrics.apOutstanding;
        totalAPOverdue += plant.metrics.apOverdue;
        totalPOs += plant.metrics.openPOs;
        totalInvoices += plant.metrics.pendingInvoices;
        totalCash += plant.metrics.cashPosition;
      }
    });

    runInAction(() => {
      this.totalAROutstanding = totalAR;
      this.totalAROverdue = totalAROverdue;
      this.totalAPOutstanding = totalAP;
      this.totalAPOverdue = totalAPOverdue;
      this.totalOpenPOs = totalPOs;
      this.totalPendingInvoices = totalInvoices;
      this.outstandingInvoices = totalAR; // Sync with existing field
      this.overduePayments = totalAROverdue;
      this.pendingPurchaseOrders = totalPOs;
      this.cashFlowPosition = totalCash;
    });
  }

  async loadCFOMetrics() {
    // Aggregate CFO metrics from plant data - will populate once plants are loaded
    this.aggregatePlantMetrics();
  }

  // Async actions
  async initializePlants() {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      // Try to load real plants from plantsStore first
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const plantsStore = this.rootStore.plantsStore as any;
      let realPlants: PlantWithMetrics[] = [];

      if (plantsStore?.entities?.length) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        realPlants = (plantsStore.entities as any[]).map((plant) => ({
          id: plant.id,
          code: plant.plantCode,
          name: plant.name,
          location: plant.city && plant.state ? `${plant.city}, ${plant.state}` : plant.address,
          manager: plant.plantManagerId ? `Manager ${plant.plantManagerId}` : undefined,
          status: plant.isActive ? "active" : "inactive",
          createdAt: plant.createdAt,
          updatedAt: plant.updatedAt,
        }));
      }

      // Add metrics to each plant from financial data
      const plantsWithMetrics = realPlants.map((plant) => {
        const realMetrics = this.calculatePlantMetricsFromFinancialData(plant.id);
        return {
          ...plant,
          metrics: realMetrics || this.getEmptyMetrics(),
        };
      });

      // Load CFO metrics from various services
      await this.loadCFOMetrics();

      runInAction(() => {
        this.plants = plantsWithMetrics;
        this.isLoading = false;
      });

      // Aggregate metrics from all plants
      this.aggregatePlantMetrics();
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load plants";
        this.isLoading = false;
      });
    }
  }

  async refreshPlantMetrics() {
    if (!this.plants.length) {
      await this.initializePlants();
      return;
    }

    runInAction(() => {
      this.isLoading = true;
    });

    try {
      // Recalculate metrics for each plant from financial data
      const updatedPlants = this.plants.map((plant) => {
        const realMetrics = this.calculatePlantMetricsFromFinancialData(plant.id);
        return {
          ...plant,
          updatedAt: new Date().toISOString(),
          metrics: realMetrics || this.getEmptyMetrics(),
        };
      });

      runInAction(() => {
        this.plants = updatedPlants;
        this.isLoading = false;
      });

      // Re-aggregate metrics
      this.aggregatePlantMetrics();
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to refresh metrics";
        this.isLoading = false;
      });
    }
  }

  navigateToPlant(plantId: string) {
    // Navigate to specific plant dashboard
    this.rootStore.routerStore.goTo("PlantDashboard", { plantId });
  }

  // Computed values are below, plants is now an observable array

  get filteredPlants(): PlantWithMetrics[] {
    let filtered = this.plants;

    // Filter by status
    if (this.filterStatus !== "all") {
      filtered = filtered.filter((plant) => plant.status === this.filterStatus);
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (plant) =>
          plant.name.toLowerCase().includes(query) ||
          plant.location?.toLowerCase().includes(query) ||
          (plant as PlantWithMetrics).code?.includes(query)
      );
    }

    return filtered;
  }

  get selectedPlant(): PlantWithMetrics | null {
    if (!this.selectedPlantId) return null;
    return (
      this.plants.find((plant) => plant.id === this.selectedPlantId) || null
    );
  }

  // Revenue computed from actual plant metrics
  get totalMonthRevenue(): number {
    return this.plants.reduce(
      (total, plant) => total + ((plant as PlantWithMetrics).metrics?.revenueMTD || 0),
      0
    );
  }

  get totalYearRevenue(): number {
    return this.plants.reduce(
      (total, plant) => total + ((plant as PlantWithMetrics).metrics?.revenueYTD || 0),
      0
    );
  }

  get totalMonthProfit(): number {
    // Estimate profit as ~20% of revenue
    return Math.round(this.totalMonthRevenue * 0.20);
  }

  get totalYearProfit(): number {
    // Estimate profit as ~20% of revenue
    return Math.round(this.totalYearRevenue * 0.20);
  }

  get activePlantCount(): number {
    return this.plants.filter((plant) => plant.status === "active").length;
  }

  get totalEmployeeCount(): number {
    // TODO: Load from HR service when available
    return 0;
  }

  get averageUtilization(): number {
    const activePlants = this.plants.filter(
      (plant) => plant.status === "active"
    );
    if (activePlants.length === 0) return 0;

    // Calculate average based on cash position vs outstanding
    const totalCash = activePlants.reduce(
      (sum, p) => sum + ((p as PlantWithMetrics).metrics?.cashPosition || 0),
      0
    );
    const totalOutstanding = activePlants.reduce(
      (sum, p) =>
        sum +
        ((p as PlantWithMetrics).metrics?.arOutstanding || 0) +
        ((p as PlantWithMetrics).metrics?.apOutstanding || 0),
      0
    );

    // Simple utilization formula: higher cash relative to outstanding = higher utilization
    if (totalOutstanding === 0) return 90;
    const ratio = totalCash / totalOutstanding;
    return Math.min(95, Math.max(50, Math.round(ratio * 50 + 40)));
  }

  // Helper methods for metric thresholds (per spec: color-coded red/yellow/green)
  getMetricStatus(
    value: number,
    thresholds: { green: number; yellow: number }
  ): "success" | "warning" | "error" {
    if (value >= thresholds.green) return "success";
    if (value >= thresholds.yellow) return "warning";
    return "error";
  }

  // AR aging status - red if overdue > 10% of outstanding
  getARStatus(): "success" | "warning" | "error" {
    if (this.totalAROutstanding === 0) return "success";
    const overdueRatio = this.totalAROverdue / this.totalAROutstanding;
    if (overdueRatio < 0.05) return "success";
    if (overdueRatio < 0.10) return "warning";
    return "error";
  }

  // AP status - green if payments under control
  getAPStatus(): "success" | "warning" | "error" {
    if (this.totalAPOutstanding === 0) return "success";
    const overdueRatio = this.totalAPOverdue / this.totalAPOutstanding;
    if (overdueRatio < 0.05) return "success";
    if (overdueRatio < 0.10) return "warning";
    return "error";
  }

  // Cash position status
  getCashStatus(): "success" | "warning" | "error" {
    // Positive cash flow is good
    if (this.cashFlowPosition > 1000000) return "success";
    if (this.cashFlowPosition > 0) return "warning";
    return "error";
  }
}
